"use client";
import { motion } from "framer-motion";
import { fadeUp, stagger } from "../../../../components/motionVariants";
import { ArrowRight } from "lucide-react";
import Link from "next/link";
import DocsPageFrame from "../../../components/DocsPageFrame";
import Callout from "../../../components/Callout";
import Screenshot from "../../../components/Screenshot";

const B = ({ children }) => (
  <span className="geist-medium text-[#111827]">{children}</span>
);

const TRANSITIONS = [
  { from: "Draft", to: "Open, Cancelled" },
  { from: "Open", to: "Closed, Draft, Cancelled" },
  {
    from: "Partially Recorded",
    to: "Closed, Cancelled — cancel is offered, but it fails if a non-cancelled Delivery Challan or Invoice already exists from this order",
  },
  { from: "Closed", to: "Cancelled" },
  { from: "Cancelled", to: "Draft, Open" },
  {
    from: "Completed",
    to: "Nothing on Mark As. The system sets this when remaining quantity hits zero after a Delivery Challan or Invoice is saved",
  },
];

const CONVERT_FROM = [
  {
    from: "Quote",
    when: "Accepted only. Convert To Sales Order sits on the quote itself",
    result:
      "Customer, items, amounts, charges, notes, and location come across. The quote stays Accepted until you save this Sales Order — then it becomes Completed",
  },
  {
    from: "Customer Order",
    when: "Accepted only. Convert sits on the Customer Order. You must pick Division, Branch, and Warehouse first — the portal order does not carry those",
    result:
      "The Customer Order becomes Completed when this Sales Order is saved, not when you click Convert",
  },
  {
    from: "Rejection Order",
    when: "PC-to-PC settle from Rejections. Converted From shows the rejection number",
    result:
      "The rejection moves to Settled when this Sales Order is saved. After save you land back on that rejection",
  },
  {
    from: "Scratch",
    when: "Add Sales Order with nothing pre-filled",
    result: "A new order. Save makes Draft; Save & Open makes Open",
  },
  {
    from: "Duplicate",
    when: "Duplicate Document on the list or the detail page",
    result:
      "Opens Add Sales Order with the same customer, items, and amounts. New order number, today's dates, Draft, no attachments",
  },
];

const CONVERT_TO = [
  {
    to: "Delivery Challan",
    when: "Open or Partially Recorded. Delivery Challans must be on your plan, and you need create permission",
    where:
      "Row action or detail Convert, or the list CONVERT menu (pick customer, then orders, then remaining lines)",
    result:
      "Opens a new Delivery Challan with this order linked. The Sales Order status changes only after that challan is saved — remaining quantity left becomes Partially Recorded; remaining quantity zero becomes Completed",
  },
  {
    to: "Invoice",
    when: "Open or Partially Recorded. You need invoice create permission",
    where: "Same as Delivery Challan — row, detail, or the list CONVERT menu",
    result:
      "Opens a new Invoice with this order linked. Status on the Sales Order updates the same way when the invoice is saved",
  },
  {
    to: "Production",
    when: "Open or Partially Recorded, Production on your plan, and this order does not already have a production order (rejected or closed ones don't count)",
    where: "Create Production on the list row or the detail page — a modal, not a new form",
    result:
      "Creates production for the selected items (service items are skipped). You pick in-stock, production, and inventory-in warehouses, plus production owner. One conversion per order — the button then says a production order already exists",
  },
];

const RULES = [
  {
    topic: "No ledger posting",
    rule: "A Sales Order never posts to the general ledger. Accounts Receivable and Sales hit when you save an Invoice (and stock movement follows Inventory Config on Delivery Challan / Invoice, not on this document)",
  },
  {
    topic: "Subscription gate",
    rule: "Only creating a Sales Order is limited by your plan. Editing, changing status, converting, duplicating, and deleting are not. Create Production is separately gated by the Production add-on",
  },
  {
    topic: "A primary bank account is required to create",
    rule: "If your company hasn't set up a primary bank account yet, you can't create a Sales Order at all — mark one under Banking → Banks with Make this primary. The bank details print on the PDF",
  },
  {
    topic: "Sales Order number",
    rule: "Auto-generated with an SO prefix unless you turn that off under Sales Order Configuration on the form. That same drawer also stores Terms & Conditions, which pre-fill every new order. Locked after the order is saved",
  },
  {
    topic: "Division, Branch, Warehouse, Customer",
    rule: "All four are required to create. After save they are locked. Converted-from Quote, Customer Order, or Rejection also lock those fields on the new form before save",
  },
  {
    topic: "Currency",
    rule: "Taken from the customer at creation (or from the quote, if this order came from one). An exchange rate field appears when that currency differs from the company base. The customer itself can't be changed later, so the currency stays put",
  },
  {
    topic: "Stock check",
    rule: "If Inventory Config has Stock Check Source = Sales Order, save is blocked when the warehouse doesn't have enough. Stock Deduct Source cannot be Sales Order — this order does not reserve or deduct stock. The Inventory Impact panel on detail only shows whether stock check was on at create time",
  },
  {
    topic: "Editing skips the overdue-invoice check",
    rule: "That check only runs when you first create an order. Editing a Draft re-checks the shipping address, the credit limit, and stock on quantity increases, but not overdue invoices",
  },
  {
    topic: "You can only edit a Draft",
    rule: "Open, Partially Recorded, Completed, Closed, and Cancelled cannot be edited. Change items and amounts while it is still Draft, then Mark As Open",
  },
  {
    topic: "Convert to DC / Invoice only from Open or Partially Recorded",
    rule: "Closed, Completed, Draft, and Cancelled cannot convert. Partially Recorded carries only remaining quantity",
  },
  {
    topic: "Closed vs Completed",
    rule: "Closed is a Mark As you pick — it stops converting even if quantity remains. Completed is set by the system when remaining quantity hits zero after a Delivery Challan or Invoice is saved. You never Mark As Completed",
  },
  {
    topic: "Open can go back to Draft",
    rule: "Mark As on an Open order offers Closed, Draft, and Cancelled. Cancelled is not a dead end — it can return to Draft or Open",
  },
  {
    topic: "Cancelling vs deleting",
    rule: "Cancel is blocked while a non-cancelled Delivery Challan or Invoice exists from this order. Delete is stricter — blocked if any Delivery Challan, Invoice, or Production Order exists from it, including cancelled documents. A production order does not block cancel",
  },
  {
    topic: "Source documents after cancel / delete",
    rule: "If this order came from a Quote, cancel or delete moves that quote back to Accepted. A Customer Order stays Completed. A Rejection Order stays Settled",
  },
  {
    topic: "Credit limit and overdue restrictions aren't Sales Order-specific",
    rule: "Settings → Credit limits holds the company defaults. Restriction Document is the amount check. Restriction Payment Terms Document is the overdue check — if Sales Order is listed, you cannot create an order while that customer has an Overdue invoice. A customer can override those defaults on their Branches tab",
  },
  {
    topic: "Attachments",
    rule: "Up to 3 files, 5 MB each. Editing a Draft adds new files to the existing ones; nothing is replaced unless you remove it",
  },
];

const sections = [
  {
    heading: "What this module does",
    subsections: [
      { heading: "1. Creating a sales order" },
      { heading: "2. Viewing, searching, and exporting" },
      { heading: "3. Editing a sales order" },
      { heading: "4. Printing and downloading" },
      { heading: "5. Changing a sales order's status" },
      { heading: "6. Converted from — what can become a Sales Order" },
      { heading: "7. Converted to — what a Sales Order can become" },
      { heading: "8. Duplicating a sales order" },
      { heading: "9. Deleting a sales order" },
    ],
  },
  { heading: "Business rules" },
];

const Definitions = ({ items }) => (
  <div className="space-y-2.5">
    {items.map((item) => (
      <p
        key={item.term}
        className="text-[16px] geist-regular text-[#374151] leading-relaxed"
      >
        <span className="geist-medium text-[#111827]">{item.term}:</span>{" "}
        {item.desc}
      </p>
    ))}
  </div>
);

export default function SalesOrdersPage() {
  return (
    <DocsPageFrame sections={sections}>
      <motion.div
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.05 }}
        variants={stagger}
      >
        <motion.div variants={fadeUp} className="mb-10">
          <h1 className="text-[30px] leading-[1.2] text-[#111827] sentient-bold mb-4">
            Sales Orders
          </h1>
          <h2 className="text-[24px] sentient-medium text-[#111827] mb-3">
            For: Anyone confirming what the customer will actually buy, before
            you deliver or invoice
          </h2>
          <p className="text-[16px] sm:text-[17px] leading-[1.75] text-[#374151] geist-regular max-w-2xl">
            A <B>Sales Order</B> is the confirmed order — items, quantities,
            prices, tax, and where it ships from. It can start from scratch, or
            carry forward an <B>Accepted Quote</B>, an <B>Accepted Customer
            Order</B>, or a <B>Rejection</B> you are settling PC-to-PC. From
            here you convert <B>Open</B> or <B>Partially Recorded</B> orders
            into a <B>Delivery Challan</B>, an <B>Invoice</B>, or{" "}
            <B>Production</B>. The order itself does not post to the ledger.
          </p>
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-0"
            className="text-[24px] sentient-medium text-[#111827] mb-6 scroll-mt-24"
          >
            What this module does
          </h2>

          <h3
            id="section-0-sub-0"
            className="text-[18px] sentient-medium text-[#111827] mb-3 scroll-mt-24"
          >
            1. Creating a sales order
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Pick <B>Division</B>, then <B>Branch</B>, then <B>Warehouse</B> —
            the rest of the form stays locked until those three are set. A
            scratch order can pre-fill those three from{" "}
            <B>Settings → Organization defaults</B>. A few checks run
            automatically the moment you save.
          </p>
          <Definitions
            items={[
              {
                term: "Customer and Shipping Address",
                desc: (
                  <>
                    The address has to be{" "}
                    <B>one of that customer&apos;s own addresses</B>. You
                    can&apos;t pick an address that belongs to someone else.
                  </>
                ),
              },
              {
                term: "Sales Order Number",
                desc: (
                  <>
                    Auto-generated unless you turn that off under{" "}
                    <B>Sales Order Configuration</B> on the form (prefix and next
                    number). That same drawer stores <B>Terms & Conditions</B>,
                    which pre-fill every new order. Locked after save.
                  </>
                ),
              },
              {
                term: "Sales Order Tag",
                desc: (
                  <>
                    Optional. Tags come from All Master →{" "}
                    <B>Sales Order Tag</B>. You can filter the list by them
                    later.
                  </>
                ),
              },
              {
                term: "Expected Shipment Date",
                desc: (
                  <>
                    Optional. Shows on the list and export so you can see what
                    is due out.
                  </>
                ),
              },
              {
                term: "Price List",
                desc: (
                  <>
                    Optional. When you pick one, item rates come from that list
                    instead of the item&apos;s default selling price. Changing
                    customer or price list may ask you to refresh rates.
                  </>
                ),
              },
              {
                term: "Currency and exchange rate",
                desc: (
                  <>
                    Currency comes from the customer, or from the quote if this
                    order was converted from one. If it differs from the
                    company base (set under <B>Settings → Currencies</B>), an{" "}
                    <B>Exchange Rate</B> field appears and you set it on this
                    order.
                  </>
                ),
              },
              {
                term: "Items, discounts, charges, and tax",
                desc: (
                  <>
                    Line items with quantity and rate. Document discount follows{" "}
                    <B>Settings → Discounts</B> — Enable Discount, then Discount
                    Applies On <B>Item Level</B> or <B>Transaction Level</B>.
                    Extra lines such as freight come from{" "}
                    <B>Settings → Additional charges</B> assigned to Sales
                    Order. GST (CGST / SGST / IGST) is calculated from customer
                    state vs company state. Round-off is available on the totals.
                  </>
                ),
              },
              {
                term: "Credit limit",
                desc: (
                  <>
                    If the customer has a credit limit that covers sales orders
                    (Restriction Document includes Sales Order), the total
                    can&apos;t push them over it. Defaults live under{" "}
                    <B>Settings → Credit limits</B> until the customer overrides
                    them. You&apos;ll be told the <B>limit</B>,{" "}
                    <B>current usage</B>, and <B>how much room is left</B> if it
                    would.
                  </>
                ),
              },
              {
                term: "Overdue invoices",
                desc: (
                  <>
                    If <B>Restriction Payment Terms Document</B> includes Sales
                    Order (on the customer, or the company default under{" "}
                    <B>Settings → Credit limits</B>), you can&apos;t create an
                    order while that customer has an <B>Overdue</B> invoice.
                    Pay it, or remove Sales Order from that list.
                  </>
                ),
              },
              {
                term: "Stock check",
                desc: (
                  <>
                    If <B>Settings → Inventory Config</B> has Stock Check Source
                    = Sales Order, save is blocked when the warehouse
                    doesn&apos;t have enough. Stock is <B>not reserved or
                    deducted</B> here — deduct is never on a Sales Order.
                  </>
                ),
              },
              {
                term: "Save vs Save & Open",
                desc: (
                  <>
                    <B>Save</B> creates a Draft. <B>Save & Open</B> is only on
                    create — saves and marks it <B>Open</B> in one step, so it
                    can be converted straight away.
                  </>
                ),
              },
            ]}
          />
          <Callout type="warning">
            You can&apos;t create a sales order at all until your company has a{" "}
            <B>primary bank account</B> set up. Mark one under Banking → Banks
            with <B>Make this primary</B>. Those bank details print on the order
            PDF, even if this particular order never becomes an invoice.
          </Callout>
          <Callout type="note">
            A sales order does <B>not</B> post to the ledger. Nothing hits
            Accounts Receivable or Sales until you convert it and save the
            Invoice.
          </Callout>
          <Screenshot caption="Screenshot: the Add Sales Order form, showing Division, Branch, Warehouse, Customer, Save, and Save & Open." />

          <h3
            id="section-0-sub-1"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            2. Viewing, searching, and exporting
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            The list and the detail page cover different needs.
          </p>
          <Definitions
            items={[
              {
                term: "Finding an order",
                desc: (
                  <>
                    Status tabs across the top, plus filters for{" "}
                    <B>date range</B>, <B>status</B>, <B>sales order tag</B>,{" "}
                    <B>customer</B>, <B>added by</B>, <B>division</B>, and{" "}
                    <B>branch</B>. Search by order number, reference, delivery
                    method, payment terms, notes, or customer name. If CRM is
                    on your plan, a Created From filter separates Books vs CRM.
                  </>
                ),
              },
              {
                term: "Export",
                desc: (
                  <>
                    Export Sales Orders from the list toolbar, using the
                    columns you currently have showing.
                  </>
                ),
              },
              {
                term: "Detail page",
                desc: (
                  <>
                    Header shows the order number and status.{" "}
                    <B>Converted From</B> names the Quote, Customer Order, or
                    Rejection if there is one. Linked Delivery Challans,
                    Invoices, and Inventory Impact sit on the same page.
                  </>
                ),
              },
            ]}
          />
          <Callout type="note">
            Sales Order-wise, item-wise, and customer-wise analyses are not on
            this screen — they live under <B>Analytics</B>.
          </Callout>
          <Screenshot caption="Screenshot: the Sales Orders list with status tabs, Convert menu, and row actions." />

          <h3
            id="section-0-sub-2"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            3. Editing a sales order
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only a <B>Draft</B> can be edited. Open, Partially Recorded,
            Completed, Closed, and Cancelled lock the form.
          </p>
          <Definitions
            items={[
              {
                term: "What stays locked even on a Draft",
                desc: (
                  <>
                    Customer, Division, Branch, Warehouse, and the order
                    number. Change items, quantities, rates, dates, tags,
                    notes, and attachments.
                  </>
                ),
              },
              {
                term: "Stock on edit",
                desc: (
                  <>
                    If stock check is on, only lines whose quantity{" "}
                    <B>increased</B> are re-checked.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Edit on a Draft sales order, with customer and location fields disabled." />

          <h3
            id="section-0-sub-3"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            4. Printing and downloading
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Use <B>Download</B> (PDF) or <B>Print</B> on the detail header,
            next to View Customer. There is no Send-by-email status on a sales
            order — Open is how it becomes convertible, not Sent.
          </p>
          <Screenshot caption="Screenshot: Download and Print on the sales order detail header, next to View Customer." />

          <h3
            id="section-0-sub-4"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            5. Changing a sales order&apos;s status
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            <B>Mark As</B> only offers the moves below. Partially Recorded and
            Completed are also set automatically when you save a Delivery
            Challan or Invoice from this order.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Mark As offers
                  </th>
                </tr>
              </thead>
              <tbody>
                {TRANSITIONS.map((t, i) => (
                  <tr
                    key={t.from}
                    className={
                      i < TRANSITIONS.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[160px]">
                      {t.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {t.to}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Callout type="note">
            <B>Closed</B> is something you choose — it stops converting even if
            quantity remains. <B>Completed</B> is something the system sets
            when remaining quantity hits zero. A <B>Cancelled</B> order
            isn&apos;t a dead end — it can be brought back to <B>Draft</B> or{" "}
            <B>Open</B>.
          </Callout>
          <Definitions
            items={[
              {
                term: "Cancelling",
                desc: (
                  <>
                    Blocked while a <B>non-cancelled</B> Delivery Challan or
                    Invoice exists from this order. You&apos;ll see which one.
                    A production order does <B>not</B> block cancel. If the
                    order came from a quote, that quote returns to{" "}
                    <B>Accepted</B>.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Mark As on an Open sales order, showing Closed, Draft, and Cancelled." />

          <h3
            id="section-0-sub-5"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            6. Converted from — what can become a Sales Order
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Conversion is started on the <B>source</B> document (or Duplicate
            / Add). Saving this Sales Order is what completes the source —
            clicking Convert only opens the form.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    From
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When / where
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What happens to the source
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_FROM.map((row, i) => (
                  <tr
                    key={row.from}
                    className={
                      i < CONVERT_FROM.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[140px]">
                      {row.from}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Callout type="tip">
            An Accepted quote can become a Sales Order <B>or</B> an Invoice —
            not both. Convert from the quote, not from this screen.
          </Callout>
          <Screenshot caption="Screenshot: Converted From on a sales order detail, showing Quote, Customer Order, or Rejection Order." />

          <h3
            id="section-0-sub-6"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            7. Converted to — what a Sales Order can become
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Only <B>Open</B> or <B>Partially Recorded</B> orders convert. You
            can raise a Delivery Challan and an Invoice from the same order
            (remaining quantity is tracked). Production is once per order.
          </p>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto mb-4">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    To
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    When
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Where you start
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    What happens
                  </th>
                </tr>
              </thead>
              <tbody>
                {CONVERT_TO.map((row, i) => (
                  <tr
                    key={row.to}
                    className={
                      i < CONVERT_TO.length - 1
                        ? "border-b border-[#F5F5F5]"
                        : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[130px]">
                      {row.to}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.when}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.where}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151] align-top">
                      {row.result}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Definitions
            items={[
              {
                term: "Remaining quantity",
                desc: (
                  <>
                    On a Partially Recorded order, convert only offers lines
                    that still have quantity left after non-cancelled Delivery
                    Challans and Invoices. Fully converted lines disappear from
                    the convert picker.
                  </>
                ),
              },
              {
                term: "List CONVERT menu",
                desc: (
                  <>
                    Convert to Delivery Challan or Convert to Invoice opens a
                    picker: customer first, then which Open / Partially
                    Recorded orders, then which remaining lines. Use this when
                    several orders for the same customer go on one document.
                  </>
                ),
              },
              {
                term: "What you cannot convert to from here",
                desc: (
                  <>
                    Not a Quote, not a Customer Order, not a Credit Note, not
                    another Sales Order. Those are separate documents.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Convert to Delivery Challan, Convert to Invoice, and Create Production on an Open sales order." />

          <h3
            id="section-0-sub-7"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            8. Duplicating a sales order
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            From the list or the detail page, <B>Duplicate Document</B> opens
            Add Sales Order with the same customer, items, and amounts.
          </p>
          <Definitions
            items={[
              {
                term: "What is left off",
                desc: (
                  <>
                    A new order number, today&apos;s dates, Draft status, and
                    no attachments. You save it as a fresh order.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: Duplicate Document on the sales order list row actions." />

          <h3
            id="section-0-sub-8"
            className="text-[18px] sentient-medium text-[#111827] mb-3 mt-8 scroll-mt-24"
          >
            9. Deleting a sales order
          </h3>
          <p className="text-[16px] geist-regular text-[#374151] leading-relaxed mb-3">
            Delete is stricter than cancel.
          </p>
          <Definitions
            items={[
              {
                term: "Blocked when",
                desc: (
                  <>
                    Any <B>Delivery Challan</B>, <B>Invoice</B>, or{" "}
                    <B>Production Order</B> exists from this order — including
                    cancelled ones. You&apos;ll be shown which document is
                    holding it.
                  </>
                ),
              },
              {
                term: "If it came from a quote",
                desc: (
                  <>
                    That quote returns to <B>Accepted</B> and the conversion
                    link is removed, so you can convert the quote again.
                  </>
                ),
              },
            ]}
          />
          <Screenshot caption="Screenshot: a delete attempt blocked because a Delivery Challan was already created from the sales order." />
        </motion.div>

        <hr className="my-8" />

        <motion.div variants={fadeUp} className="mb-12">
          <h2
            id="section-1"
            className="text-[24px] sentient-medium text-[#111827] mb-4 scroll-mt-24"
          >
            Business rules
          </h2>
          <div className="border border-[#E5E5E5] rounded-[14px] overflow-hidden bg-white overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-[#F5F5F5] bg-[#F9F9F9]">
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Topic
                  </th>
                  <th className="px-5 py-3 text-[12px] geist-medium text-[#6B7280] uppercase tracking-wide">
                    Rule
                  </th>
                </tr>
              </thead>
              <tbody>
                {RULES.map((r, i) => (
                  <tr
                    key={r.topic}
                    className={
                      i < RULES.length - 1 ? "border-b border-[#F5F5F5]" : ""
                    }
                  >
                    <td className="px-5 py-3 text-[14px] geist-medium text-[#111827] align-top w-[220px]">
                      {r.topic}
                    </td>
                    <td className="px-5 py-3 text-[14px] geist-regular text-[#374151]">
                      {r.rule}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>

        <motion.div
          variants={fadeUp}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Link
            href="/documentation/books/sales/quotes"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Quotes <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              An Accepted quote can become this Sales Order
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/customer-orders"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Customer Orders <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              An Accepted portal order converts only into a Sales Order
            </p>
          </Link>
          <Link
            href="/documentation/books/sales/delivery-challans"
            className="group flex-1 border border-[#E5E5E5] hover:border-black rounded-[12px] p-4 bg-white transition-all"
          >
            <p className="text-[14px] sentient-medium text-[#111827] group-hover:text-black transition-colors mb-1 flex items-center gap-1.5">
              Delivery Challans <ArrowRight className="w-3.5 h-3.5" />
            </p>
            <p className="text-[12px] geist-regular text-[#374151]">
              One of the three documents an Open order can become
            </p>
          </Link>
        </motion.div>
      </motion.div>
    </DocsPageFrame>
  );
}
